import { Plan } from 'api/savings-plans';
import { SavingsPlan } from 'interfaces/customer';
import { PetProfile } from 'types/pet-profile';

interface TransformedPet {
  pet_type: string;
  name?: string;
  age?: string;
  weight?: string;
  needs?: string[];
}

/**
 * Creates a transformed pet with only meaningful values
 */
const createTransformedPet = (
  petType: string,
  name?: string | null,
  age?: string | null,
  weight?: number | null,
  needs?: string[] | null
): TransformedPet => {
  const result: TransformedPet = {
    pet_type: petType,
  };

  // Only add optional fields if they have meaningful values
  if (name) result.name = name;
  if (age) result.age = age;
  if (weight) result.weight = String(weight);
  if (needs && needs.length > 0) result.needs = needs;

  return result;
};

/**
 * Transforms pet profiles to match the savings plan backend format.
 * Only includes fields with meaningful values (no null, undefined, or empty arrays).
 */
export const transformPetsForSavingsPlan = (pets?: PetProfile[]): TransformedPet[] | undefined => {
  if (!pets || pets.length === 0) return undefined;

  return pets.map((pet) =>
    createTransformedPet(
      pet.petType,
      pet.name,
      pet.selectedAge,
      pet.selectedWeight,
      pet.selectedNeeds
    )
  );
};

interface GetSavingsPlanLoadingStateParams {
  activePlan: Plan | SavingsPlan | null;
  authLoading: boolean;
  customerHasPlan: boolean;
  isPlanFromCustomer: boolean;
  swrIsLoading: boolean;
  customerSavingsPlan?: SavingsPlan | null;
}

interface SavingsPlanLoadingState {
  isLoading: boolean;
  isWaitingForCustomerPlan: boolean;
  isWaitingForAuth: boolean;
}

/**
 * Calculate loading state for savings plan
 * Determines when to show loader vs actual content
 */
export const getSavingsPlanLoadingState = ({
  activePlan,
  authLoading,
  customerHasPlan,
  isPlanFromCustomer,
  swrIsLoading,
  customerSavingsPlan,
}: GetSavingsPlanLoadingStateParams): SavingsPlanLoadingState => {
  // Check if activePlan matches the customer's plan
  const activePlanMatchesCustomer =
    customerHasPlan &&
    customerSavingsPlan &&
    activePlan &&
    activePlan.planName === customerSavingsPlan.planName &&
    isPlanFromCustomer;

  // Wait for customer plan if customer has a plan but activePlan doesn't match yet
  const isWaitingForCustomerPlan = customerHasPlan && !activePlanMatchesCustomer;

  // Combined loading state: SWR loading OR waiting for customer plan OR waiting for auth
  const isLoading = swrIsLoading || isWaitingForCustomerPlan || authLoading;

  return {
    isLoading,
    isWaitingForCustomerPlan,
    isWaitingForAuth: authLoading,
  };
};

/**
 * Finds the updated plan that matches the active plan name
 */
export const findUpdatedPlan = (availablePlans: Plan[], activePlanName: string): Plan | undefined =>
  availablePlans.find((plan) => plan.planName === activePlanName);

/**
 * Checks if variant IDs have changed between old and new plan
 */
export const hasVariantChanged = (oldVariantId: string, newVariantId: string): boolean =>
  oldVariantId !== newVariantId;

interface RemoveFromLocalStorageParams {
  localCartKey: string;
  oldVariantId: string;
}

/**
 * Removes a savings plan from localStorage cart by variant ID
 * Returns true if removal was successful, false otherwise
 */
export const removeFromLocalStorage = ({
  localCartKey,
  oldVariantId,
}: RemoveFromLocalStorageParams): boolean => {
  try {
    const localCartJson = localStorage.getItem(localCartKey);

    if (!localCartJson) {
      return false;
    }

    const localCart: Array<{
      variantId: string;
      quantity: number;
    }> = JSON.parse(localCartJson);

    const oldPlanIndex = localCart.findIndex((item) => String(item.variantId) === oldVariantId);

    if (oldPlanIndex !== -1) {
      const filteredCart = localCart.filter((item) => String(item.variantId) !== oldVariantId);

      localStorage.setItem(localCartKey, JSON.stringify(filteredCart));
      return true;
    }

    return false;
  } catch (error) {
    console.error('[useSavingsPlan] Error removing from localStorage:', error);
    return false;
  }
};

/**
 * Finds a cart item by variant ID
 * Generic function that works with any item type that has a variantId property
 */
export const findCartItemByVariantId = <T extends { variantId: string | number }>(
  items: T[],
  variantId: string
): T | undefined => items.find((item) => String(item.variantId) === variantId);
